/**
 * TrackImporterDuckDB — DuckDB-wasm facade for reading flight tracks out of
 * GPX / KML / GeoJSON / CSV / TSV files with the `spatial` extension's
 * `ST_Read` function. The WASM bundle and worker are loaded on first call
 * via dynamic import so the main app bundle stays light.
 *
 * This module is intentionally browser-only \u2014 unit tests exercise the
 * pure-TS parsers in `track-parsers/` directly. If `@duckdb/duckdb-wasm`
 * fails to load (CSP restrictions, test env), the TrackImporter falls
 * back to those parsers.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TrackFormat, TrackPoint } from './track-parsers/track-types';

const M_TO_FT = 3.28084;
const MPS_TO_KTS = 1.94384;

export interface LoadedDuckDB {
  db: any;
  conn: any;
}

let loadingPromise: Promise<LoadedDuckDB> | null = null;

export async function loadDuckDB(): Promise<LoadedDuckDB> {
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    // Dynamic import so the WASM runtime only ships when the review panel
    // is actually opened.
    const duckdb: any = await import('@duckdb/duckdb-wasm');
    const bundles = duckdb.getJsDelivrBundles();
    const bundle = await duckdb.selectBundle(bundles);

    // The worker must be loaded from a blob URL so Vite's module graph
    // doesn't try to bundle it.
    const workerUrl = URL.createObjectURL(
      new Blob([`importScripts("${bundle.mainWorker}");`], { type: 'text/javascript' }),
    );
    const worker = new Worker(workerUrl);
    const logger = new duckdb.ConsoleLogger();
    const db = new duckdb.AsyncDuckDB(logger, worker);
    await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
    URL.revokeObjectURL(workerUrl);

    const conn = await db.connect();
    // Install and load the spatial extension (OGR-backed readers)
    await conn.query("INSTALL spatial;");
    await conn.query("LOAD spatial;");
    return { db, conn };
  })().catch((e) => {
    loadingPromise = null;
    throw e;
  });

  return loadingPromise;
}

/** Write a File's bytes into DuckDB's virtual filesystem as `vpath`. */
export async function registerFile(db: any, file: File, vpath: string): Promise<void> {
  const buf = new Uint8Array(await file.arrayBuffer());
  await db.registerFileBuffer(vpath, buf);
}

function rowToTrackPoint(row: Record<string, any>): TrackPoint | null {
  const lon = row.lon ?? row.longitude ?? row.x;
  const lat = row.lat ?? row.latitude ?? row.y;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;

  const altM = row.altitude_m ?? row.ele ?? null;
  const altFtRaw = row.altitude_ft ?? null;
  let altitudeFt: number | undefined;
  if (Number.isFinite(altFtRaw)) altitudeFt = altFtRaw;
  else if (Number.isFinite(altM)) altitudeFt = altM * M_TO_FT;

  const spdKtsRaw = row.speed_kts ?? row.gs ?? null;
  const spdMpsRaw = row.speed_mps ?? row.speed ?? null;
  let groundSpeedKts: number | undefined;
  if (Number.isFinite(spdKtsRaw)) groundSpeedKts = spdKtsRaw;
  else if (Number.isFinite(spdMpsRaw)) groundSpeedKts = spdMpsRaw * MPS_TO_KTS;

  const heading = row.heading ?? row.course ?? row.track ?? null;

  let timestamp: number | undefined;
  const tRaw = row.time ?? row.timestamp ?? row.datetime ?? null;
  if (typeof tRaw === 'bigint') timestamp = Number(tRaw);
  else if (Number.isFinite(tRaw)) timestamp = tRaw as number;
  else if (typeof tRaw === 'string') {
    const parsed = Date.parse(tRaw);
    if (Number.isFinite(parsed)) timestamp = parsed;
  } else if (tRaw instanceof Date) timestamp = tRaw.getTime();

  return {
    latitude: lat,
    longitude: lon,
    altitudeFt,
    groundSpeedKts,
    heading: Number.isFinite(heading) ? (heading as number) : undefined,
    timestamp,
  };
}

/**
 * Read an OGR-supported file (GPX, KML, GeoJSON) via `ST_Read`. The spatial
 * extension exposes one row per geometry with all source attributes as
 * columns; we project them to our TrackPoint columns before normalising.
 */
async function readWithStRead(
  conn: any,
  vpath: string,
): Promise<TrackPoint[]> {
  // Explode geometry into lon/lat and coalesce common attribute names the
  // GDAL GPX/KML drivers emit. Columns that don't exist yield NULL, which
  // the row normaliser treats as absent.
  const sql = `
    WITH src AS (
      SELECT *,
             ST_X(geom) AS lon_calc,
             ST_Y(geom) AS lat_calc
      FROM ST_Read('${vpath}')
    )
    SELECT
      lon_calc AS lon,
      lat_calc AS lat,
      TRY_CAST(ele AS DOUBLE)        AS altitude_m,
      TRY_CAST("time" AS VARCHAR)    AS "time",
      TRY_CAST(speed AS DOUBLE)      AS speed_mps,
      TRY_CAST(course AS DOUBLE)     AS heading
    FROM src
    ORDER BY "time" NULLS LAST;
  `;
  const result = await conn.query(sql);
  const rows = result.toArray().map((r: any) => r.toJSON ? r.toJSON() : r);
  const out: TrackPoint[] = [];
  for (const row of rows) {
    const p = rowToTrackPoint(row);
    if (p) out.push(p);
  }
  return out;
}

/**
 * Read a CSV / TSV via DuckDB's auto-detecting CSV reader and normalise.
 * DuckDB picks up quoting, delimiter, and types automatically.
 */
async function readCsv(conn: any, vpath: string, delimiter: string): Promise<TrackPoint[]> {
  const sql = `
    SELECT *
    FROM read_csv_auto('${vpath}', delim='${delimiter}', header=TRUE)
  `;
  const result = await conn.query(sql);
  const rows = result.toArray().map((r: any) => r.toJSON ? r.toJSON() : r);
  // CSV column names vary; lowercase everything and let rowToTrackPoint
  // match any of the supported aliases.
  const out: TrackPoint[] = [];
  for (const row of rows) {
    const lower: Record<string, any> = {};
    for (const [k, v] of Object.entries(row)) lower[k.toLowerCase()] = v;
    const p = rowToTrackPoint(lower);
    if (p) out.push(p);
  }
  return out;
}

export async function importWithDuckDB(file: File, format: TrackFormat): Promise<TrackPoint[]> {
  const { db, conn } = await loadDuckDB();
  const extMap: Record<TrackFormat, string> = {
    gpx: 'gpx', kml: 'kml', geojson: 'geojson', csv: 'csv', tsv: 'tsv',
  };
  const vpath = `/flight-track-${Date.now()}.${extMap[format]}`;
  await registerFile(db, file, vpath);

  try {
    if (format === 'csv') return await readCsv(conn, vpath, ',');
    if (format === 'tsv') return await readCsv(conn, vpath, '\t');
    return await readWithStRead(conn, vpath);
  } finally {
    try { await db.dropFile(vpath); } catch { /* ok */ }
  }
}
